<template>
  <div>
    <CModal
      :show.sync="show"
      @close="close"
      centered
    >
      <template #header>
        <div>
          <div class="d-flex align-items-center">
            <CIcon name="cipBriefcase01" size="custom-size" width="36" height="36"
                   class="mr-3 icon-circle icon-circle--primary"/>
            <div>
              <h5 class="modal-title">
                <translate translate-context="payout.companyManagement">Company management</translate>
              </h5>
            </div>
          </div>
        </div>
        <CButtonClose @click="close"/>
      </template>
      <template #default>
        <!-- Loading -->
        <div v-if="isLoading" class="text-center py-4">
          <CSpinner color="primary"/>
        </div>

        <!-- CM Selector (multi-CM, no preselection) -->
        <div v-else-if="showCmSelector" class="py-2">
          <p class="mb-3">
            <translate translate-context="payout.companyManagement">Select the company to manage:</translate>
          </p>
          <ContractManagerSelector
            :contract-managers="contractManagers"
            :selected-id="selectedCmId"
            @select="onSelectCm"
          />
        </div>

        <!-- Read-only view for certified -->
        <div v-else-if="showCertifiedView">
          <CCard class="mb-3" style="background-color: #F7FAFF;">
            <CCardBody>
              <div class="d-flex align-items-center mb-3">
                <CBadge color="success" class="badge-outline">
                  <translate translate-context="payout.companyManagement">Approved</translate>
                </CBadge>
              </div>
              <p class="mb-2" style="color: #302F44;">
                <translate translate-context="payout.companyManagement">
                  Your company has been verified. You are now ready to receive payments.
                </translate>
              </p>
              <div class="mb-2 d-flex align-items-center justify-content-between"
                   v-if="certification.region === 'eu' && certification.vat_number">
                <div class="font-weight-medium">
                  <translate translate-context="payout.companyManagement">VAT</translate>
                </div>
                <div class="text-uppercase">{{ certification.vat_number }}</div>
              </div>
              <CButton :to="{ name: 'payout' }" color="primary" class="mt-3" @click.native="close">
                <translate translate-context="payout.companyManagement">Go to Payouts</translate>
              </CButton>
            </CCardBody>
          </CCard>
        </div>

        <!-- No CMs found -->
        <div v-else-if="noCMs" class="text-center py-4">
          <p class="text-muted">
            <translate translate-context="payout.companyManagement">No companies found.</translate>
          </p>
        </div>
      </template>

      <template #footer>
        <CButton color="secondary" @click="close">
          <translate translate-context="payout.companyManagement">Close</translate>
        </CButton>
      </template>
    </CModal>

    <CertificationModal
      ref="certificationModal"
      :is-submitting="isSaving"
      :can-submit="canSubmit"
      @submit="handleCertificationSubmit"
    />
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import ContractManagerSelector from './ContractManagerSelector.vue'
import CertificationModal from './CertificationModal.vue'
import {CERTIFICATION_STATUS} from '../constants'
import eventBus from '@/domain/core/constant/eventBus'
import teleport from "@/mixins/teleport";

export default {
  name: 'CompanyManagementModal',
  mixins: [teleport],
  components: {
    ContractManagerSelector,
    CertificationModal
  },
  data() {
    return {
      show: false,
      isLoading: false,
      isSaving: false,
      selectedCmId: null
    }
  },
  computed: {
    ...mapGetters('payout', [
      'contractManagers',
      'certification',
      'certificationStatus'
    ]),
    showCmSelector() {
      return this.contractManagers.length > 1 && !this.selectedCmId
    },
    showCertifiedView() {
      return this.selectedCmId && this.certification.status === CERTIFICATION_STATUS.CERTIFIED
    },
    noCMs() {
      return this.contractManagers.length === 0
    },
    canSubmit() {
      return this.hasPerm('contract_manager.accounting.certification.submit')
    },
    canValidateVat() {
      return this.hasPerm('contract_manager.accounting.certification.validate_vat')
    },
  },
  methods: {
    ...mapActions('payout', [
      'setSelectedCmId',
      'fetchCertificationScopes',
      'fetchCertification',
      'submitCertification',
      'updateCertification'
    ]),
    ...mapActions('login', ['fetchConfig']),

    /**
     * Apri la modale.
     * @param {number|null} cmId - Se passato, skippa il discovery e usa direttamente questo CM
     */
    async open(cmId = null) {
      this.selectedCmId = null

      if (cmId) {
        // Chiamato dal payout con CM già noto — apri direttamente
        await this.onSelectCm(cmId)
      } else {
        // Chiamato dall'header — discovery CM
        this.show = true
        this.isLoading = true

        try {
          const contractManagers = await this.fetchCertificationScopes()

          if (contractManagers.length === 1) {
            await this.onSelectCm(contractManagers[0].id)
          }
        } catch (e) {
          this.notifyError(e)
        } finally {
          this.isLoading = false
        }
      }
    },

    close() {
      this.show = false
    },

    async onSelectCm(cmId) {
      this.selectedCmId = cmId
      this.isLoading = true

      try {
        this.setSelectedCmId(cmId)

        // Fetch config permessi e certificazione in parallelo
        await Promise.all([
          this.fetchConfig({type: 'certification_contract_manager', id: cmId}),
          this.fetchCertification()
        ])

        const status = this.certification.status

        if (status === CERTIFICATION_STATUS.CERTIFIED) {
          this.show = true
          this.isLoading = false
        } else {
          this.isLoading = false
          this.show = false
          this.$nextTick(() => {
            this.$refs.certificationModal.open(
              this.certification.id ? this.certification : null
            )
          })
        }
      } catch (e) {
        this.isLoading = false
        this.notifyError(e)
      }
    },

    async handleCertificationSubmit(formData) {
      if (!this.selectedCmId) return

      this.isSaving = true
      try {
        if (this.certification.id) {
          await this.updateCertification(formData)
        } else {
          await this.submitCertification(formData)
        }

        this.$refs.certificationModal.close()
        this.notifySuccess(this.$pgettext('payout.companyManagement', 'Certification submitted successfully'))
        this.$emit('updated')
        this.emitEvent(eventBus.certification.submitted)
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.isSaving = false
      }
    }
  }
}
</script>
